// ========== VoxPay Service - EMAIL/WEB ONLY (NO CALLS) ==========

class VoxPayService {
  constructor() {
    // 🔒 SECURITY: ONLY environment variables - NO fallback credentials
    this.config = {
      apiKey: process.env.REACT_APP_VOXPAY_API_KEY,
      jwt: process.env.REACT_APP_VOXPAY_JWT,
      campaignId: process.env.REACT_APP_VOXPAY_CAMPAIGN_ID,
      apiUrl: 'https://api.voxpay.ai',
      sandboxUrl: 'https://api.voxpay.io',
      environment: process.env.REACT_APP_VOXPAY_ENV || 'production',
      returnUrl: `${window.location.origin}/lunch-payment-return`,
      cancelUrl: `${window.location.origin}/lunch-payment-cancel`,
      currency: 'EUR',
      // ✅ EMAIL/WEB ONLY - NO PHONE CALLS
      paymentMode: 'EMAIL_WEB_ONLY'
    };

    // 🔒 SECURITY: Validate environment variables on startup
    this.validateEnvironment();

    console.log('💳 VoxPayService EMAIL/WEB Mode:', {
      apiUrl: this.getApiUrl(),
      environment: this.config.environment,
      hasApiKey: !!this.config.apiKey,
      hasJWT: !!this.config.jwt,
      hasCampaignId: !!this.config.campaignId,
      paymentMode: this.config.paymentMode,
      origin: window.location.origin,
      // 🔍 DEBUG: Show first/last chars of credentials
      apiKeyPreview: this.config.apiKey ? `${this.config.apiKey.substring(0, 4)}...${this.config.apiKey.substring(this.config.apiKey.length - 4)}` : 'MISSING',
      campaignPreview: this.config.campaignId ? `${this.config.campaignId.substring(0, 8)}...` : 'MISSING'
    });
  }

  /**
   * 🔒 SECURITY: Validate required environment variables
   */
  validateEnvironment() {
    const missingVars = [];
    
    if (!this.config.apiKey) {
      missingVars.push('REACT_APP_VOXPAY_API_KEY');
    }
    
    if (!this.config.campaignId) {
      missingVars.push('REACT_APP_VOXPAY_CAMPAIGN_ID');
    }
    
    if (missingVars.length > 0) {
      const errorMessage = `🔒 SECURITY ERROR: Missing required VoxPay variables: ${missingVars.join(', ')}`;
      console.error(errorMessage);
      throw new Error(`VoxPay configuration incomplete. Missing: ${missingVars.join(', ')}`);
    }
    
    // 🔍 Validate credential format
    this.validateCredentialFormat();
    
    console.log('✅ VoxPay EMAIL/WEB environment variables validated successfully');
  }

  /**
   * 🔍 Validate credential format
   */
  validateCredentialFormat() {
    const warnings = [];
    
    // Check API Key format
    if (this.config.apiKey && this.config.apiKey.length < 32) {
      warnings.push('⚠️ API Key seems too short (< 32 chars)');
    }

    // Check Campaign ID format (should be UUID)
    if (this.config.campaignId) {
      const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
      if (!uuidRegex.test(this.config.campaignId)) {
        warnings.push('⚠️ Campaign ID is not a valid UUID format');
      }
    }

    if (warnings.length > 0) {
      console.warn('🚨 VoxPay EMAIL/WEB Credential Warnings:');
      warnings.forEach(warning => console.warn(warning));
    }
  }

  /**
   * 🔧 Test VoxPay connection for EMAIL/WEB payments
   */
  async testVoxPayConnection() {
    console.log('🧪 Testing VoxPay EMAIL/WEB connection...');
    
    try {
      const response = await fetch(`${this.getApiUrl()}/payments?page=1&per_page=1`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.config.apiKey}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        },
        mode: 'cors'
      });

      console.log(`🔍 VoxPay EMAIL/WEB test response: ${response.status} ${response.statusText}`);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ VoxPay EMAIL/WEB authentication successful');
        return { 
          success: true, 
          method: 'email_web',
          data,
          authHeader: `Bearer ${this.config.apiKey}`,
          paymentMode: this.config.paymentMode
        };
      } else {
        let errorDetails = `HTTP ${response.status}`;
        try {
          const errorData = await response.text();
          errorDetails += ` - ${errorData}`;
        } catch (e) {
          // Ignore parsing errors
        }
        
        return { 
          success: false, 
          method: 'email_web',
          error: errorDetails
        };
      }
    } catch (error) {
      console.error('❌ VoxPay EMAIL/WEB test network error:', error);
      return { 
        success: false, 
        method: 'email_web',
        error: `Network error: ${error.message}`
      };
    }
  }

  /**
   * 🔧 Validate amount and prevent 0€ payments
   */
  validateAmount(amount) {
    console.log('💰 Validating payment amount:', amount);
    
    if (!amount && amount !== 0) {
      throw new Error('Montant requis pour le paiement');
    }
    
    const numAmount = parseFloat(amount);
    
    if (isNaN(numAmount)) {
      throw new Error(`Montant invalide: ${amount}`);
    }
    
    if (numAmount <= 0) {
      throw new Error(`Le montant doit être supérieur à 0€ (reçu: ${numAmount}€)`);
    }
    
    console.log('✅ Amount validated for EMAIL/WEB payment:', numAmount);
    return numAmount;
  }

  /**
   * 🔧 Generate short transaction ID (max 36 chars)
   * Si une référence est fournie, l'utiliser (tronquée à 36 chars)
   */
  generateShortTransactionId(reservationId, customReference = null) {
    // Si une référence personnalisée est fournie, l'utiliser
    if (customReference) {
      const cleanRef = customReference.substring(0, 36);
      console.log(`🔗 EMAIL/WEB transaction ID (custom): ${cleanRef} (${cleanRef.length} chars)`);
      return cleanRef;
    }

    // Sinon, générer une référence automatique
    const timestamp = Date.now().toString().slice(-6);
    const random = Math.random().toString(36).substring(2, 5).toUpperCase();
    const shortId = `MSP_EW_${reservationId}_${timestamp}_${random}`; // EW = Email/Web

    if (shortId.length > 36) {
      return `MSP_EW_${timestamp}_${random}`;
    }

    console.log(`🔗 EMAIL/WEB transaction ID: ${shortId} (${shortId.length} chars)`);
    return shortId;
  }

  /**
   * 🔧 Generate secure URLs for EMAIL/WEB payments
   */
  generateSecureUrls(paymentData, transactionId, validatedAmount) {
    const baseSuccessUrl = `${this.config.returnUrl}/${paymentData.uniqueLink}`;
    const baseCancelUrl = `${this.config.cancelUrl}/${paymentData.uniqueLink}`;
    
    const successParams = new URLSearchParams({
      status: 'success',
      transaction_id: transactionId,
      amount: validatedAmount.toString(),
      verified: 'true',
      payment_method: 'voxpay_email_web',
      timestamp: Date.now().toString(),
      reservation_id: paymentData.reservationId.toString()
    });
    
    const failureParams = new URLSearchParams({
      status: 'failure',
      transaction_id: transactionId,
      amount: validatedAmount.toString(),
      payment_method: 'voxpay_email_web',
      timestamp: Date.now().toString(),
      reservation_id: paymentData.reservationId.toString()
    });
    
    const cancelParams = new URLSearchParams({
      status: 'cancelled',
      transaction_id: transactionId,
      amount: validatedAmount.toString(),
      payment_method: 'voxpay_email_web',
      timestamp: Date.now().toString(),
      reservation_id: paymentData.reservationId.toString()
    });
    
    const urls = {
      success_url: `${baseSuccessUrl}?${successParams.toString()}`,
      failure_url: `${baseCancelUrl}?${failureParams.toString()}`,
      cancel_url: `${baseCancelUrl}?${cancelParams.toString()}`
    };
    
    console.log('🔗 Generated secure URLs for EMAIL/WEB payment');
    return urls;
  }

  getApiUrl() {
    return this.config.environment === 'sandbox' ? this.config.sandboxUrl : this.config.apiUrl;
  }

  /**
   * 🔧 Create EMAIL/WEB payment link with VoxPay
   */
  async createPaymentLink(paymentData) {
    try {
      console.log('💳 === CREATING VOXPAY EMAIL/WEB PAYMENT ===');
      console.log('💳 Payment data:', {
        customerEmail: paymentData.customerEmail,
        customerName: paymentData.customerName,
        rawAmount: paymentData.amount,
        reservationId: paymentData.reservationId,
        mode: 'EMAIL_WEB_ONLY'
      });
      
      // Step 1: Validate amount
      const validatedAmount = this.validateAmount(paymentData.amount);
      
      // Step 2: Email validation (REQUIRED for EMAIL/WEB)
      if (!paymentData.customerEmail || paymentData.customerEmail.trim() === '') {
        throw new Error('Email client requis pour le paiement EMAIL/WEB VoxPay.');
      }

      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(paymentData.customerEmail)) {
        throw new Error('Format d\'email invalide pour VoxPay EMAIL/WEB.');
      }

      console.log('✅ Email validated for EMAIL/WEB:', paymentData.customerEmail);
      console.log('✅ Amount secured for EMAIL/WEB:', validatedAmount, '€');
      
      // Step 3: Test VoxPay connection
      const connectionTest = await this.testVoxPayConnection();
      if (!connectionTest.success) {
        throw new Error(`VoxPay EMAIL/WEB authentication failed: ${connectionTest.error}`);
      }

      console.log(`✅ VoxPay EMAIL/WEB connection successful`);

      // Step 4: Generate transaction and URLs
      // Utiliser la référence personnalisée si fournie
      const transactionId = this.generateShortTransactionId(paymentData.reservationId, paymentData.reference);
      const secureUrls = this.generateSecureUrls(paymentData, transactionId, validatedAmount);

      // Step 5: Create EMAIL/WEB payment
      const result = await this.createSecureEmailWebPayment(
        paymentData, 
        transactionId, 
        validatedAmount,
        secureUrls,
        connectionTest.authHeader
      );

      if (result.success) {
        console.log('✅ VoxPay EMAIL/WEB payment created successfully');
        return result;
      } else {
        throw new Error('EMAIL/WEB payment creation failed');
      }

    } catch (error) {
      console.error('❌ VoxPay EMAIL/WEB payment creation failed:', error);
      return this.handlePaymentError(error, paymentData);
    }
  }

  /**
   * 🔧 Create EMAIL/WEB payment with VoxPay
   */
  async createSecureEmailWebPayment(paymentData, transactionId, validatedAmount, secureUrls, authHeader) {
    console.log(`💳 Creating VoxPay EMAIL/WEB payment with amount: ${validatedAmount}€`);

    // 🔥 VoxPay EMAIL/WEB PAYLOAD
    const paymentPayload = {
      // ✅ REQUIRED: cinematic_id for EMAIL/WEB payment (6 = email payment)
      cinematic_id: 6,
      
      // ✅ REQUIRED: amount and currency
      amount: validatedAmount,
      currency: "EUR",
      
      // ✅ REQUIRED: customer information (EMAIL MANDATORY)
      customer_email: paymentData.customerEmail.trim().toLowerCase(),
      customer_firstname: paymentData.customerName?.split(' ')[0]?.trim() || 'Client',
      customer_lastname: paymentData.customerName?.split(' ').slice(1).join(' ')?.trim() || 'Master Ski Pilou',
      
      // ✅ OPTIONAL: phone (not required for EMAIL/WEB)
      customer_phone: paymentData.customerPhone || '',
      
      // ✅ REQUIRED: campaign_id
      campaign_id: this.config.campaignId,
      
      // ✅ RECOMMENDED: order details
      order_details: {
        number_of_product: "1",
        billing: {
          street: "Master Ski Pilou",
          zip_code: "97133", 
          city: "Saint Barthélemy",
          country: "BL"
        }
      },
      
      // ✅ OPTIONAL: internal reference (max 36 chars)
      reference: transactionId,
      
      // ✅ OPTIONAL: description (utiliser celle fournie ou générer une par défaut)
      description: paymentData.description || `Master Ski Pilou - ${paymentData.boatName || 'Charter'} ${validatedAmount}€ - ${paymentData.charterDate}`,
      
      // ✅ OPTIONAL: return URLs
      success_url: secureUrls.success_url,
      failure_url: secureUrls.failure_url,
      cancel_url: secureUrls.cancel_url,
      
      // ✅ OPTIONAL: auto redirect
      auto_redirect: true,
      
      // ✅ OPTIONAL: metadata
      extra: {
        boat_name: paymentData.boatName?.toString() || '',
        charter_date: paymentData.charterDate?.toString() || '',
        reservation_id: paymentData.reservationId?.toString() || '',
        source: 'master-ski-pilou-email-web',
        trip_type: paymentData.tripType?.toString() || '',
        payment_mode: 'EMAIL_WEB_ONLY',
        timestamp: new Date().toISOString()
      }
    };

    console.log(`📤 VoxPay EMAIL/WEB payload:`, {
      cinematic_id: paymentPayload.cinematic_id,
      amount: paymentPayload.amount,
      currency: paymentPayload.currency,
      customer_email: paymentPayload.customer_email,
      customer_phone: paymentPayload.customer_phone || 'Not provided',
      campaign_id: paymentPayload.campaign_id,
      reference_length: paymentPayload.reference?.length || 0,
      payment_mode: 'EMAIL_WEB_ONLY'
    });

    // Validate payload before sending
    if (!this.validateEmailWebPayload(paymentPayload)) {
      throw new Error('Invalid EMAIL/WEB payload format');
    }

    const response = await fetch(`${this.getApiUrl()}/payments`, {
      method: 'POST',
      headers: {
        'Authorization': authHeader,
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      mode: 'cors',
      body: JSON.stringify(paymentPayload)
    });

    console.log(`📥 VoxPay EMAIL/WEB response: ${response.status} ${response.statusText}`);

    if (!response.ok) {
      const errorText = await response.text();
      console.error(`❌ VoxPay EMAIL/WEB payment error:`, errorText);
      throw new Error(`VoxPay EMAIL/WEB payment failed: ${response.status} - ${errorText}`);
    }

    const result = await response.json();
    console.log(`✅ VoxPay EMAIL/WEB payment created:`, {
      id: result.id,
      amount: validatedAmount,
      status: result.status,
      link: result.link || result.payment_url,
      expires_at: result.expires_at,
      payment_mode: 'EMAIL_WEB_ONLY'
    });

    return {
      success: true,
      paymentUrl: result.link || result.payment_url || result.url,
      transactionId: transactionId,
      voxpayId: result.id,
      securedAmount: validatedAmount,
      expiresAt: result.expires_at,
      provider: 'voxpay-email-web',
      paymentMode: 'EMAIL_WEB_ONLY',
      voxpayData: result,
      note: 'VoxPay EMAIL/WEB payment created successfully'
    };
  }

  /**
   * 🔧 Validate EMAIL/WEB payload format
   */
  validateEmailWebPayload(payload) {
    const required = [
      'cinematic_id',
      'amount', 
      'currency',
      'customer_email',
      'customer_firstname',
      'customer_lastname',
      'campaign_id'
    ];
    
    const missing = required.filter(field => !payload[field]);
    
    if (missing.length > 0) {
      console.error('❌ Missing required EMAIL/WEB fields:', missing);
      return false;
    }
    
    // Specific validations for EMAIL/WEB
    if (payload.cinematic_id !== 6) {
      console.warn('⚠️ cinematic_id should be 6 for EMAIL/WEB payments');
    }
    
    if (typeof payload.amount !== 'number') {
      console.error('❌ amount must be a number for EMAIL/WEB');
      return false;
    }
    
    if (!payload.customer_email || !payload.customer_email.includes('@')) {
      console.error('❌ valid customer_email required for EMAIL/WEB');
      return false;
    }
    
    console.log('✅ EMAIL/WEB payload validation passed');
    return true;
  }

  /**
   * 🔧 Check payment status (EMAIL/WEB)
   */
  async checkPaymentStatus(voxpayId) {
    try {
      const response = await fetch(`${this.getApiUrl()}/payments/${voxpayId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${this.config.apiKey}`,
          'Accept': 'application/json',
        },
        mode: 'cors'
      });

      if (response.ok) {
        const data = await response.json();
        return {
          success: true,
          status: data.status,
          amount: data.amount,
          paymentMode: 'EMAIL_WEB',
          data
        };
      } else {
        return {
          success: false,
          error: `HTTP ${response.status}`
        };
      }
    } catch (error) {
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * 🔧 Handle payment errors with detailed diagnostics
   */
  handlePaymentError(error, paymentData) {
    console.group('🔍 VoxPay EMAIL/WEB Error Analysis');
    console.log('📡 Network:', navigator.onLine ? 'Online' : 'Offline');
    console.log('🔗 API URL:', this.getApiUrl());
    console.log('🔑 Has API Key:', !!this.config.apiKey);
    console.log('🔑 Has Campaign ID:', !!this.config.campaignId);
    console.log('📧 Customer Email:', paymentData.customerEmail ? '***@***.***' : 'N/A');
    console.log('💰 Amount:', paymentData.amount);
    console.log('📱 Payment Mode:', 'EMAIL_WEB_ONLY');
    console.log('🚫 Error:', error.message);
    console.groupEnd();

    return {
      success: false,
      error: error.message,
      paymentMode: 'EMAIL_WEB_ONLY',
      diagnostic: {
        online: navigator.onLine,
        apiUrl: this.getApiUrl(),
        hasCredentials: {
          apiKey: !!this.config.apiKey,
          campaignId: !!this.config.campaignId
        },
        timestamp: new Date().toISOString(),
        amount: paymentData.amount,
        email: paymentData.customerEmail ? 'provided' : 'missing',
        troubleshooting: [
          'Check .env file exists in project root',
          'Verify VoxPay credentials in dashboard', 
          'Restart development server after .env changes',
          'Check browser console for validation errors',
          'Ensure customer email is valid for EMAIL/WEB payments',
          'Verify cinematic_id=6 is enabled in VoxPay account'
        ]
      }
    };
  }
}

export { VoxPayService };

// ========== VOXPAY EMAIL/WEB CONFIGURATION GUIDE ==========
/*
🚨 VOXPAY EMAIL/WEB IMPLEMENTATION

✅ CHANGES MADE:
1. ✅ Removed all phone call references
2. ✅ Email is now mandatory (not phone)
3. ✅ cinematic_id = 6 (email payment)
4. ✅ Payment mode clearly marked as EMAIL_WEB_ONLY
5. ✅ Enhanced email validation
6. ✅ Specific error handling for EMAIL/WEB

🔧 ENVIRONMENT VARIABLES REQUIRED:
REACT_APP_VOXPAY_API_KEY=nwOVzTOydNCZRutvBiOKCbArNxD4fY2OolIBcuCom57ZxmYoa9zPRvsSYh6B
REACT_APP_VOXPAY_CAMPAIGN_ID=4cc8a2e9-746d-43c4-a6aa-75258dcfe78d
REACT_APP_VOXPAY_ENV=production
REACT_APP_VOXPAY_JWT=sirAiub1OFdLHdl3mIRF1Km86haghniv75M1n06W4XgSQ6GibDRfQdds2OcR

🚀 READY FOR EMAIL/WEB PAYMENTS:
- Email/Web payments only (no phone calls)
- Enhanced email validation
- Secure URL generation
- Proper error handling
- Payment status checking

🔍 DEBUGGING:
- All logs marked with EMAIL/WEB
- Payment mode tracked throughout
- Enhanced error diagnostics
- Clear separation from phone payments
*/